<template>
  <div>
    <p>
      <strong>Virtual Primary Care Privacy Notice</strong>
    </p>
    <p>
      <strong>Effective Date:</strong> June 5, 2025
    </p>
    <p>
      <strong>Introduction</strong>
    </p>
    <p>
      Spectrum.Life (“Spectrum.Life”, “we”, “us”, or “our”) is committed to
      respecting your privacy and the confidentiality of your personal
      information. This Privacy Notice explains how we collect, use, store, and
      protect personal data in relation to our Virtual Primary Care service. It is
      a standalone policy focusing on the virtual primary healthcare services we
      provide (such as online doctor consultations, digital health assessments,
      prescription fulfillment, and related services). We have updated this notice
      to reflect the latest EU and UK data protection requirements as of 2025,
      including compliance with the EU General Data Protection Regulation (GDPR)
      and the UK Data Protection Act 2018, while adhering to guidance from
      supervisory authorities like the Irish Data Protection Commission (DPC) and
      the UK Information Commissioner’s Office (ICO). We strive to write in plain
      language while maintaining legal robustness. This ensures the notice is
      accessible to both individual patients (e.g. employees or insurance members
      using the service) and corporate stakeholders (e.g. employers or insurers
      sponsoring the service). By using the Virtual Primary Care service, you
      acknowledge the practices described in this Privacy Notice.
    </p>
    <p>
      <strong>Who We Are (Data Controller Information)</strong>
    </p>
    <p>
      Spectrum Wellness Limited (trading as Spectrum.Life) and Spectrum Wellness
      UK Limited are the entities responsible for your personal data when you use
      our services, depending on your location. If you are based in the European
      Union (for example, Ireland or elsewhere in the EEA), the Data Controller is
      Spectrum Wellness Limited (Irish Company Registration No. 555787), with a
      registered office at 95 Merrion Square, Dublin 2, Ireland. If you are based
      in the United Kingdom, the Data Controller is Spectrum Wellness UK Limited
      (Company No. 10670700), with a registered office at Habib House, 9 Stevenson
      Square, Manchester, M1 1DB. Spectrum.Life determines the purposes and means
      of processing your personal data (i.e. we act as the “data controller” for
      the Virtual Primary Care service). In certain instances – for example, if we
      deliver services on behalf of a client organisation (such as your employer
      or insurer) – we may act as a data processor, processing your data under the
      client’s instructions, with that client organisation being the data
      controller for those specific arrangements. In all cases, we handle your
      data lawfully and securely as described in this notice.
    </p>
    <p>
      <strong>Contacting Us:</strong> If you have any questions about this notice
      or wish to exercise your data protection rights (explained below), you can
      contact our Data Protection Officer (DPO). The DPO is your point of contact
      for privacy queries or concerns.
    </p>
    <ul>
      <li>
        Email: gdprspectrumlife@spectrum.life
      </li>
      <li>
        Postal Address: Data Protection Officer, Spectrum Wellness Limited, 95
        Merrion Square, Dublin 2, Ireland.
      </li>
    </ul>
    <p>
      We will do our best to address your requests or concerns. If you are not
      satisfied, you have the right to lodge a complaint with a supervisory
      authority. If you reside in Ireland or the EU, you can contact the Irish
      Data Protection Commission (see dataprotection.ie), and if you are in the UK
      you can contact the Information Commissioner’s Office (see ico.org.uk). We
      would appreciate the opportunity to resolve your concerns directly, so
      please consider reaching out to us.
    </p>
    <p>
      <strong>Services Covered by This Notice</strong>
    </p>
    <p>
      This Privacy Notice applies specifically to Spectrum.Life’s Virtual Primary
      Care service. This service provides you with access to primary healthcare in
      a virtual setting – for example, booking and attending online GP or nurse
      consultations, using symptom-checkers or triage tools, receiving medical
      advice and referrals, and obtaining electronic prescriptions through our
      platform. It covers all personal data processing in the Virtual Primary Care
      portal and related services, including any digital tools or integrations
      (such as prescription delivery services or AI-driven health assistants) that
      form part of the Virtual Primary Care offering. This notice is intended to
      complement Spectrum.Life’s general privacy notice by providing more focused
      detail on how data is handled for Virtual Primary Care. Both individual
      users of the service and representatives of organisations offering the
      service to their people should find the relevant information here.
    </p>
    <p>
      <strong>Personal Data We Collect and Why</strong>
    </p>
    <p>
      To provide our Virtual Primary Care services, we must collect and process
      certain personal data about you. We limit collection to what is necessary
      for your care and related purposes, and we treat special categories (like
      health data) with extra care. Here are the types of personal data we collect
      and the reasons we collect them:
    </p>
    <ul>
      <li>
        <strong>Identity and Contact Information:</strong> This includes basic
        personal details such as your name, date of birth, gender, postal
        address, email address, phone number, and any unique identifiers (for
        example, an employee or membership ID number from your employer or
        insurer). We collect this information to verify your identity, register
        you for the service, communicate with you about appointments or results,
        and ensure we are consulting the right person. It helps us confirm your
        eligibility (e.g. that you are part of an organisation’s health plan)
        and to contact you with service-related notices (like appointment
        reminders or follow-up communications).
      </li>
      <li>
        <strong>Health and Medical Information (Special Category Data):</strong>
        This is the core of the Virtual Primary Care service. It includes the
        health information you or your medical providers provide us, such as
        your medical history, symptoms, medications, allergies, clinical notes
        from consultations, diagnoses, test results, and prescription details.
        For example, when you have a virtual consultation, the doctor or nurse
        will create a record of the visit (symptoms discussed, findings, advice
        given). If you use a symptom checker or AI triage tool, the symptoms or
        answers you input are also health data. We collect and use this
        information solely to provide you with medical care – for medical
        assessment, diagnosis, advice, treatment, and follow-up. Because this is
        “special category” data under GDPR, we apply heightened protection (as
        detailed throughout this notice). We only collect what is relevant for
        your care and we do not collect data about criminal offenses or
        unrelated matters (for instance, we do <em>not</em> need or ask for any
        criminal record information in providing healthcare). Any health
        assessments or questionnaires you complete through our platform are used
        to inform your care plan and are treated confidentially.
      </li>
      <li>
        <strong>Service Usage and Technical Data:</strong> When you use our
        Virtual Primary Care portal or app, we collect certain technical
        information automatically. This includes data like your device type,
        operating system, browser type, IP address, login times, and usage logs
        of how you navigate the platform. We also gather information on actions
        you take in the portal (e.g. pages visited, buttons clicked) and any
        error reports if something goes wrong. We collect this data through log
        files and cookies or similar tracking technologies (explained in the
        <em>Cookies and Tracking</em> section below). This technical and usage
        data is collected to ensure the service works properly, to keep your
        session secure (for example, keeping you logged in securely as you move
        through pages), and to help us understand and improve the performance of
        our platform (such as fixing bugs or improving interface design). We may
        use aggregated usage data to analyse overall trends (e.g. to see how
        many users access certain features) in line with our legitimate
        interests to improve the service. Any analytics are done in a way that
        does not identify you personally whenever possible (we use anonymization
        or aggregation for broad analysis).
      </li>
      <li>
        <strong>Communication Data:</strong> If you communicate with us or with
        clinicians through the platform, we will collect that information as
        well. This includes messages or inquiries you send via chat functions,
        emails or texts you send to our support team, and any feedback you
        provide (such as satisfaction surveys after a consultation). We keep
        these communications to respond to your requests and improve our
        services. For example, keeping a record of your customer support chat
        helps us address any follow-up issues and train our support staff. If
        you call our support or triage line, the call may be recorded (with
        notice) for quality and training purposes – see Audio/Video Recordings
        below.
      </li>
      <li>
        <strong>Audio/Video Recordings (Consultations):</strong> Our Virtual
        Primary Care service may involve phone or video consultations with
        medical professionals. In general, we do not record your video or audio
        calls without a good reason. By default, consultations are private and
        live (no recording) unless explicitly stated otherwise. However, there
        are scenarios where we might record, for example: if we have a clinical
        quality assurance program or you consent to a recorded consultation (for
        instance, to allow you to play back a medical advice session, or for our
        internal training of practitioners). In our internal planning, we have
        identified the potential to record audio or video in the platform, but
        any such recording would be transparently communicated to you and done
        only in compliance with the law. Typically, if a call is recorded, you
        will be informed at the start and given the option to consent or
        decline. Recorded consultations (if any) would be stored securely as
        part of your health record and treated with the same confidentiality as
        written notes. If you prefer not to be recorded, you will be able to opt
        out or simply not use any features involving recording. (Note: Routine
        medical consultations are generally not recorded by us, and we rely on
        clinicians’ written notes as the official record of the consultation.)
      </li>
      <li>
        <strong>Payment and Billing Information:</strong> In most cases, your
        Virtual Primary Care access is provided by a sponsoring organisation
        (like your employer or insurer), meaning you typically do not pay out of
        pocket at the time of service. However, if there are scenarios where you
        make a payment (for example, if you purchase an additional service not
        covered by your sponsor, or if a co-payment is required for a
        prescription delivery), we or our payment processor would collect
        necessary payment details (e.g. credit card information or billing
        contact info). We use accredited payment processors to handle payments
        securely, and we do not store full financial account numbers on our
        systems except possibly the last few digits or a transaction ID for
        record-keeping. Any payment data is processed only for billing purposes
        and legal compliance (e.g. tax records), and we apply strict security to
        it.
      </li>
      <li>
        <strong>Cookies and Tracking Technologies Data:</strong> When you use
        the Virtual Primary Care web portal or mobile app, we use cookies and
        similar technologies to collect data about your device and interactions.
        Cookies are small text files stored on your device to remember your
        preferences and actions. For example, we use cookies to keep you logged
        in as you navigate between pages so that you don’t have to re-enter your
        credentials repeatedly. We may also use cookies or pixels to gather
        analytics on how the service is used (e.g. which features are most
        popular) and to optimize page loading and error fixing. Some cookies are
        essential for the service to work (more details in
        <em>
          Cookies and Tracking
        </em>
        section). The information collected via cookies can include your IP
        address, browser type, device identifiers, and usage patterns on our
        site. We collect this to ensure security, provide the service
        functionality, and improve user experience. We do not use invasive
        tracking or advertising profiling in the healthcare portal – any
        marketing-related cookies would only be used with appropriate consent,
        and primarily our focus is on operational cookies.
      </li>
    </ul>
    <p>
      If we collect any other data not listed above, we will inform you at the
      time of collection. We do <em>not</em> collect data that is not needed for
      the Virtual Primary Care service. In particular, we do not seek to collect
      any data about your race/ethnicity, political opinions, religious beliefs,
      sex life, or criminal history for this service unless it is directly
      relevant to your healthcare and you provide it (for instance, information
      about your ethnicity might be relevant to certain medical risk factors, but
      we would only note such details if you volunteer them in a health context).
      Our aim is to be transparent and ask only for what we truly need to serve
      you. If you have questions about why certain information is requested,
      please ask us.
    </p>
    <p>
      <strong>How We Use Your Data (Purposes of Processing)</strong>
    </p>
    <p>
      We use the personal data collected in the Virtual Primary Care service to
      serve you and operate our services. In simpler terms, we process your
      information for specific, legitimate purposes, and we do not use it in ways
      incompatible with those purposes. Below is a summary of the main purposes
      for which we process your personal data and how it benefits you (and/or our
      corporate clients):
    </p>
    <ul>
      <li>
        <strong>Providing You Healthcare Services:</strong> First and foremost,
        we use your data to deliver the virtual healthcare you have requested.
        This includes scheduling appointments, verifying your identity at login
        or when you connect with a clinician, reviewing your medical history and
        symptoms to provide a consultation, making medical assessments and
        diagnoses, and recommending treatments or referrals. For example, a
        doctor will use the information you provide about your symptoms and
        medical history to give you medical advice and possibly issue a
        prescription. We also use your contact information to send you
        appointment confirmations, reminders, or follow-up communications
        regarding your care. Without processing your personal and health data,
        we could not safely or effectively provide medical care to you.
      </li>
      <li>
        <strong>Electronic Prescriptions and Pharmacy Fulfillment:</strong> If
        during a consultation you are prescribed medication, our platform will
        use your relevant data to facilitate that prescription. We partner with
        a secure electronic prescription service (Signature RX) to send your
        prescription to a pharmacy of your choice. We will share the minimum
        necessary information with Signature RX – typically your identity (name,
        date of birth), contact details, and the prescription details (medicine
        prescribed, dosage, etc.) – so that the pharmacy can verify and dispense
        your medication. This process is done in compliance with healthcare
        regulations and with security measures to protect your data. Your
        prescription information is used only to fill your prescription and for
        related safety checks, and it is not used for any other purpose (like
        marketing) by the pharmacy or Signature RX.
      </li>
      <li>
        <strong>Maintaining Your Health Records:</strong> We keep records of
        your virtual consultations and health information in our Electronic
        Health Record (EHR) system (powered by a secure Salesforce platform).
        This allows our clinical team to have access to your up-to-date medical
        history each time you use the service, ensuring continuity of care. For
        instance, if you have a consultation in January and another in June, the
        clinician can review the notes from January to provide better-informed
        advice in June. We use these records internally among the healthcare
        team for ongoing treatment and care coordination. They are kept
        confidential and accessed on a need-to-know basis by the clinicians and
        support staff involved in your care.
      </li>
      <li>
        <strong>Communicating with You:</strong> We use your contact information
        to communicate service-related information. This includes sending
        appointment reminders, notifications about lab results or prescription
        readiness, follow-up care instructions, and responses to any inquiries
        you send us. We may also send general service announcements, such as
        changes to our hours of operation or updates to this Privacy Notice. If
        you reach out for technical support or have a question about using the
        platform, we will use your data to respond and help you. In all cases,
        we tailor our communications to be relevant and respectful. We will not
        send you marketing emails about unrelated services unless you have
        explicitly opted-in to such communications. (If you <em>do</em> opt in
        to wellness newsletters or promotional updates, you can opt out at any
        time, and we do not share your contact details with third-party
        marketers.)
      </li>
      <li>
        <strong>Service Improvement and Analytics:</strong> Internally, we may
        use data (mostly in aggregated or anonymised form) to understand and
        improve our Virtual Primary Care service. For example, we might look at
        overall statistics such as how many consultations are performed per
        month, common technical issues users encounter, or general health trends
        (e.g. an increase in flu-related consultations during winter). This
        helps us allocate resources and introduce new features or content that
        meet our users’ needs. Any analysis for improvement is done in line with
        our legitimate interests in running and enhancing a safe, effective
        service. Importantly, when reviewing trends or producing reports for
        internal use, we strip out personal identifiers. For instance, we might
        report “100 users had a consultation this month for cold/flu symptoms”
        without needing to know which specific individuals those were.
        Similarly, if we analyse user navigation to improve our interface, we
        focus on patterns (like “X% of users had trouble finding the
        prescription status page”) rather than on any one user’s behaviour.
        These measures ensure your privacy is protected while we improve our
        offerings.
      </li>
      <li>
        <strong>Reporting to Your Sponsor (Employer or Insurer):</strong> If
        your Virtual Primary Care access is provided through your employer or
        insurance company, we may provide that sponsoring organisation with
        periodic aggregated reports about service usage. Employers and insurers
        have an interest in understanding how the service is used (to see its
        value and to ensure it’s meeting members’ needs). However, we do this in
        a privacy-protective way: reports to organisations
        <em>
          only contain anonymised or aggregated data
        </em>
        , and do not reveal your personal identity or personal health details to
        your employer or insurer. For example, an employer might receive a
        quarterly report stating, “X% of employees utilized the virtual GP
        service this quarter, top general categories were dermatology issues
        (20%), respiratory (15%), etc.” These stats would not name any
        individuals or detail anyone’s specific case. The purpose is to help the
        sponsor evaluate the program’s effectiveness while preserving individual
        confidentiality. We understand how sensitive it is to ensure that
        personal medical information is not shared back with your employer.
        Unless you have given explicit consent for a specific disclosure, your
        employer will not know the names of people using the service or the
        details of any individual’s consultation. Insurers, if funding the
        service, may receive similar anonymous trend data. The only time an
        insurer might receive identifiable info is if needed for claims
        processing (for example, if your plan requires the insurer to process
        each consultation as a claim). In such cases, we would share the minimal
        data needed (e.g. your policy/member number and the fact <em>that</em>
        you had a consultation on a certain date) to facilitate payment. They
        would not receive the content of your consultation notes. We will make
        sure any such sharing for claims is done securely and in accordance with
        privacy law and insurance regulations.
      </li>
      <li>
        <strong>Ensuring Security and Preventing Fraud:</strong> We also process
        some data to keep our services and users safe. This includes using
        technical data like device identifiers, IP addresses, and log-in history
        to detect and prevent suspicious or malicious activity on our platform.
        For example, we might use your IP and log-in attempts to recognize if
        someone is trying to brute-force access your account, or to block a
        suspected fraudulent user. We also maintain audit logs of certain
        actions (like changes made to health records) to ensure accountability
        and traceability. These measures help protect your data from
        unauthorised access and help us meet our legal obligations under
        cybersecurity laws (such as the EU NIS2 Directive). We process this data
        under legitimate interests and legal obligations to maintain a secure
        service environment.
      </li>
      <li>
        <strong>Compliance with Legal Obligations:</strong> Like any healthcare
        provider or business, we are subject to various legal requirements. We
        may process your data when necessary to comply with laws and
        regulations. For instance, medical professionals have obligations to
        retain health records for a certain period, to report certain
        communicable diseases or adverse drug reactions to health authorities,
        or to cooperate with lawful orders (like court orders or regulatory
        inquiries). If we are required by law to disclose or retain your
        information, we will do so, but only to the extent required and with due
        regard for your privacy. Another example is financial record-keeping: if
        we issue invoices or receipts (say to your insurer), we must keep those
        records for tax and audit purposes. Such records might contain minimal
        personal data (like your name on an invoice) and we hold them as
        required by law. We also process data as needed to handle any legal
        claims or disputes (e.g., if there is an investigation or a need to
        defend against a claim, we may preserve and review relevant data). We
        will notify you where we are permitted to, if any third-party requests
        for your data are made (such as a law enforcement request), unless that
        notification is legally prohibited.
      </li>
      <li>
        <strong>Anonymised Research and Development:</strong> Separately from
        your individual care, we may use anonymised health data to contribute to
        medical research or to develop better healthcare algorithms. This would
        never involve information that identifies you. If we ever analyse
        outcomes or look for insights (for example, to study the effectiveness
        of virtual consultations on reducing in-person doctor visits), we would
        remove personal identifiers or aggregate the data so individuals cannot
        be singled out. In some cases, we might use secure statistical analysis
        under Article 9(2)(j) GDPR (research and statistics) if applicable, but
        typically we rely on anonymisation so that GDPR no longer applies to the
        data (truly anonymised data is not “personal data”). We would also
        ensure any such projects comply with ethics and privacy standards, and
        where required, we would seek your consent (for example, if we ever
        wanted to use your identified data in a research study, we’d ask you
        first; but our default is to use anonymised data sets).
      </li>
    </ul>
    <p>
      In summary, we use your personal data strictly to deliver and improve the
      Virtual Primary Care service. We do <em>not</em> use it for any unrelated
      purposes like selling your information or advertising third-party products
      to you. We do not engage in any profiling that would affect you without
      human involvement (see the section on AI and automated tools). If we ever
      need to process your data for a new purpose not covered in this notice, we
      will inform you and, if required, seek your consent before doing so.
    </p>
    <p>
      <strong>Legal Bases for Processing Your Data</strong>
    </p>
    <p>
      Under data protection laws, every use of personal data must have a legal
      justification (a “lawful basis”). This section explains the legal grounds we
      rely on to process your data in the Virtual Primary Care service, as
      required by GDPR and UK law. Because health data is sensitive, we identify
      both a basis under Article 6 GDPR (for personal data in general) and a
      condition under Article 9 GDPR (specific to special category health data)
      for our processing activities. We ensure that at least one of the following
      legal bases applies to each purpose described above:
    </p>
    <ul>
      <li>
        <strong>Performance of a Contract (GDPR Article 6(1)(b)):</strong> In
        most cases, our processing of your personal data is necessary for the
        performance of a contract – namely, providing the healthcare service
        that you have requested. When you register for and use Virtual Primary
        Care, you enter into an agreement (even if the service is paid by your
        employer or insurer, you as the user agree to our terms to receive
        care). We need to process your data to fulfill our obligations in that
        agreement, such as giving medical advice, issuing a prescription you
        asked for, or managing your account. If we could not use your data, we
        would not be able to deliver the service you expect. This basis covers
        most of the routine processing related to providing consultations and
        associated services. It’s important to note that even when an employer
        or insurer is the one who arranged the service, the service being
        delivered is to you as the end-user, so we view you as party to the
        implied contract of service usage.
      </li>
      <li>
        <strong>Legitimate Interests (GDPR Article 6(1)(f)):</strong> We process
        certain data under the legitimate interests basis, carefully balanced
        against your rights. Our legitimate interests include running and
        improving a secure and effective healthcare service, and supporting the
        sponsoring organisations in evaluating the service (in
        privacy-preserving ways). For example, using aggregated analytics to
        improve features, or sending non-essential communications within the
        service (like a satisfaction survey), or providing anonymous usage
        reports to an employer, can be considered in our legitimate interests.
        We ensure that these interests are not overridden by your rights and
        freedoms: we use minimal data, anonymise where possible, and provide
        opt-outs where feasible (for instance, you can choose not to answer
        optional surveys). We also rely on legitimate interests for certain
        security-related processing – e.g., analysing IP addresses to prevent
        fraud is something we have a legitimate interest in doing to protect the
        platform. When we rely on this basis, we take into account your
        expectations and ensure no processing is done that you wouldn’t
        reasonably expect in the context of a health service. Example: It’s in
        our interest (and yours) that our platform is reliable, so we might use
        your usage data to detect usability issues (like if many users drop off
        at a certain step, indicating a potential glitch). We consider this a
        legitimate interest that also benefits users, and we do so in a way that
        doesn’t unjustifiably impact your privacy (only aggregated data is
        viewed, etc.). If you have any objections to processing that we justify
        on legitimate interests, you have the right to object (see
        <em>
          Your Rights
        </em>
        section), and we will consider your request seriously.
      </li>
      <li>
        <strong>Legal Obligation (GDPR Article 6(1)(c)):</strong> In some
        situations, we need to process your data to comply with a legal
        obligation to which we are subject. This could include laws and
        regulations related to healthcare (such as retaining medical records for
        a minimum period, or providing information to licensing bodies), tax
        laws (retaining transaction records), or data protection laws themselves
        (responding to your requests under GDPR). For instance, Irish healthcare
        guidelines may mandate that we keep medical consultation records for at
        least 7 years; this is a legal obligation we must follow. If a court
        subpoena or regulatory order requires us to produce certain data, that
        is another legal obligation. When we process data for these reasons,
        Article 6(1)(c) is our lawful basis. We will only do so as necessary and
        will inform you where possible (for example, if we’re compelled by law
        to disclose data to a public authority, we’ll let you know unless the
        law forbids it). In the DPIA for our service, we confirmed that
        compliance with such legal requirements is part of our data processing
        justification.
      </li>
      <li>
        <strong>Vital Interests (GDPR Article 6(1)(d)):</strong> This basis is
        about life-and-death or serious emergencies. We don’t typically rely on
        “vital interests” for planned care, but in a rare scenario it could
        apply – for example, if during a virtual consultation you are in a
        life-threatening situation and unable to consent, we might share
        information with an emergency medical service to save your (or someone
        else’s) life. This would be considered necessary to protect vital
        interests. Another example: if we have reason to believe you are at
        imminent risk of severe harm and you cannot provide consent (maybe you
        become unresponsive during a call), we might break confidentiality to
        the extent necessary to get you help (such as contacting emergency
        responders and providing location info). This is aligned with both
        medical ethics and allowed under GDPR’s vital interest ground. Again,
        this would be an exceptional circumstance – our usual operations don’t
        rely on this, but it’s important to mention that the law does allow data
        use or sharing without consent in extreme emergencies where vital
        interests are at stake.
      </li>
      <li>
        <strong>Consent (GDPR Article 6(1)(a)):</strong> Generally, for core
        healthcare services we do not rely on consent as the legal basis
        (because usually other bases like contract or health care necessity
        apply). In fact, under GDPR, explicit consent is one way to justify
        processing health data, but not the only way. However, we do rely on
        consent in certain scenarios – particularly for anything optional or
        where you have a genuine choice. For instance, if we ever want to use
        your identifiable data in a case study or for marketing testimonials, we
        would ask for your consent. If we introduce new optional features (say,
        a feature to share your consultation summary with your personal GP), we
        would likely ask for your consent before doing so. Also, for cookies
        that are not strictly necessary (like analytics or marketing cookies),
        we seek your consent via the cookie banner/preferences when required by
        law. In short, when consent is the appropriate basis, we will ask you
        clearly and you have the right to refuse or withdraw consent at any
        time. We will never assume consent from silence or pre-ticked boxes – it
        will be explicit. Note that for special category data (health data), if
        we rely on consent, it must be explicit consent (GDPR Article 9(2)(a)).
        One example where we might use explicit consent is during account
        registration: we may ask you to agree to a consent statement for us to
        handle your health data to provide the service. While we could
        alternatively rely on the healthcare provision basis (Art 9(2)(h)),
        obtaining your explicit consent adds an extra layer of transparency and
        choice for you. Rest assured, if you do not consent or if you withdraw
        consent later, we will stop the processing in question and there will be
        no penalty except that we might not be able to provide certain aspects
        of the service (for example, if you don’t consent to us processing your
        health data at all, we cannot provide medical care). But you are free to
        make that choice, and we will make it as easy as possible to withdraw
        consent (see <em>Your Rights</em> below).
      </li>
    </ul>
    <p>
      For Special Category Data (Health Data) under GDPR Article 9, we primarily
      rely on the following conditions to lawfully process it:
    </p>
    <ul>
      <li>
        <strong>Provision of Healthcare (GDPR Article 9(2)(h)):</strong> This
        provision allows the processing of health data when it is necessary for
        “medical diagnosis, the provision of health care or treatment or the
        management of health care systems and services” when done by or under
        the responsibility of a professional subject to confidentiality (like a
        doctor or nurse). This is very much applicable to our service – our
        doctors, nurses, and healthcare staff collect and use your health
        information in order to provide you with care. They are all bound by
        professional confidentiality (and by contractual confidentiality
        obligations with us). This is a core legal condition that permits us to
        handle your health data without needing your explicit consent each time,
        because it’s for the purpose of treating you. It’s recognised both under
        EU and UK law that health professionals must be able to use patient data
        to give treatment. Therefore, most of our health data processing is
        justified by Article 9(2)(h). Additionally, local laws (in Ireland, the
        Data Protection Act 2018; in the UK, Schedule 1 of the DPA 2018)
        reinforce this by specifying conditions for health data processing. We
        ensure that appropriate safeguards (like access controls and
        confidentiality) are in place as required by these laws when using this
        basis.
      </li>
      <li>
        <strong>Explicit Consent (GDPR Article 9(2)(a)):</strong> As mentioned
        above, we may also rely on your explicit consent for certain uses of
        your health data. For example, if you choose to use an AI symptom
        checker that is integrated from a third-party provider, we might present
        a consent screen for you to agree to that process. Or if we were to
        participate in a medical research registry and wanted to include your
        data, we would only do so with your explicit consent. By obtaining
        explicit consent, we ensure you are fully aware and agreeable to that
        particular use of your health information. You have the right to refuse
        or withdraw consent, and we will honor that (noting that it doesn’t
        retroactively undo processing already done while consent was in place,
        but we will cease further processing). We will <em>not</em> use your
        health data for any secondary purpose that isn’t covered by one of these
        legal bases without getting your clear consent.
      </li>
      <li>
        <strong>Vital Interests (GDPR Article 9(2)(c)):</strong> In an emergency
        where you cannot consent, we might invoke this condition to process or
        share health data to protect your or someone else’s life. For instance,
        sharing your allergy information with paramedics if you’re unconscious.
        This is a seldom-used basis and only for critical emergencies.
      </li>
      <li>
        <strong>Public Health (GDPR Article 9(2)(i)):</strong> If ever relevant,
        this allows processing for public health interests (like controlling
        disease outbreaks) under official authority. It’s unlikely to apply in
        our day-to-day service except if, say, public health laws mandate
        reporting of certain disease cases. If such a scenario arises, we’d
        ensure it’s done under proper legal authority.
      </li>
      <li>
        <strong>Legal Claims (GDPR Article 9(2)(f)):</strong> We could rely on
        this if we need to process health data to establish, exercise or defend
        legal claims. For example, if there was a malpractice claim, we’d use
        the medical records (which are health data) in our defence or as
        required by a court. This is allowed under GDPR.
      </li>
    </ul>
    <p>
      In practice, the primary bases for our Virtual Primary Care processing are:
      for personal data – Contract (providing the service) and Legitimate
      Interests (service improvement, security, reporting), along with Legal
      Obligations where applicable; and for health data – Healthcare Provision and
      sometimes Explicit Consent. We ensure any processing is covered by these
      bases. If you have questions about the legal basis for a specific processing
      activity, please contact us and we’ll clarify it for you. We have conducted
      a Data Protection Impact Assessment (DPIA) for this service, which confirms
      the chosen legal bases and demonstrates compliance with GDPR’s principles.
    </p>
    <p>
      <strong>How We Share Your Data (Disclosure to Third Parties)</strong>
    </p>
    <p>
      We treat your personal data with care and confidentiality. We do not sell
      your data to anyone. We only share data with third parties in a few
      circumstances, primarily to help deliver the Virtual Primary Care service or
      when required by law. Whenever we share data, we ensure appropriate
      safeguards (such as contracts and encryption) are in place to protect it.
      Here we explain the categories of recipients who may receive your data and
      why:
    </p>
    <ul>
      <li>
        <strong>Healthcare Providers and Medical Professionals:</strong> Your
        data will be shared with the doctors, nurses, or other licensed
        practitioners who provide the consultations and medical services to you.
        These may include Spectrum.Life’s in-house clinicians or trusted partner
        clinicians we arrange to see you. All such providers are bound by strict
        confidentiality obligations (by law and by contract with us). They
        access your information only as needed to perform their clinical duties
        – for example, a GP will review your medical history and notes in order
        to treat you. In some cases, if your care requires referral or
        escalation, we may share relevant information with other healthcare
        providers with your consent or at your direction (for instance, if you
        want us to send a referral letter to a specialist or to your own GP). We
        will not share your health records with outside doctors without your
        approval, except in emergency circumstances as described earlier.
      </li>
      <li>
        <strong>Your Organisation (Employer or Insurer):</strong> As noted in
        the Reporting to Your Sponsor section, we do <em>not</em> disclose your
        personal medical information to your employer or insurance company in
        the normal course of the Virtual Primary Care service. They will only
        receive anonymous, aggregated usage statistics to understand service
        utilization. For example, an employer might be told that “10% of
        eligible employees used a virtual GP this year” or an insurer might see
        that “500 consultations occurred, with the majority for common
        illnesses,” without any names or personal details attached. These
        reports help them see the value of the service, but they cannot see
        individual data. The only potential exception is if your insurer
        requires data for claims: in that case, minimal identifying data (like
        an insurance ID and date of service) might be shared for billing. Even
        then, we would not share clinical details. We have agreements in place
        with corporate clients to ensure they understand and commit that they
        will not attempt to access individuals’ health data through us. If an
        employer or insurer requests personal data beyond what we normally
        provide, we will either refuse or only comply if we have your explicit
        consent or a lawful necessity. <em>Bottom line:</em> your employer
        doesn’t know what you discuss with the doctor, and even your
        participation is only known to them in aggregate unless you choose to
        tell them.
      </li>
      <li>
        <strong>Signature RX (Prescription Partner):</strong> We integrate with
        Signature RX, a third-party electronic prescription service, to
        streamline the process of getting medications to you. When a doctor
        writes a prescription during your virtual consultation, that
        prescription is processed through the Signature RX system to be sent to
        a pharmacy. In doing so, we share the necessary information for the
        prescription: typically, your identifying details (name, DOB, contact
        info), and the prescription information (drug name, dosage, quantity,
        and possibly your chosen pharmacy). Signature RX uses this information
        to securely transmit the prescription to a pharmacy or to generate an
        electronic prescription token for you. They do not use this data for any
        other purpose – it’s strictly for dispensing your medication. Signature
        RX operates under a data processing agreement with us, meaning they are
        a data processor acting on our behalf. They are obligated to protect
        your data to GDPR standards, keep it confidential, and only process it
        as needed to fulfil the prescription service. All prescription data
        exchanges are encrypted and comply with healthcare regulations.
        Signature RX has confirmed its compliance with GDPR, the UK Data
        Protection Act, and healthcare standards for handling sensitive health
        data. In short, we use Signature RX to make your life easier (no need to
        pick up a paper prescription), and your information is handled with care
        during that process. If you prefer not to use the integrated
        e-prescription service, let your clinician know; alternative
        arrangements (like providing a prescription directly to you) can be
        made.
      </li>
      <li>
        <strong>Salesforce (Electronic Health Record Platform):</strong> We use
        a secure cloud-based platform (built on Salesforce Health Cloud or a
        similar Salesforce infrastructure) as our electronic health record (EHR)
        system to store and manage your health data. Salesforce acts as a data
        processor for us in this context. This means your consultation notes,
        medical history, and other records are stored on Salesforce’s servers
        (within the EEA or UK – see <em>Data Transfers</em> below) but remain
        under our control. Salesforce is a reputable enterprise platform with
        strong security certifications (including compliance with ISO 27001 and
        other standards). They are contractually bound to protect your data, not
        to access or use it for any purpose except storing it and making it
        available to authorized Spectrum.Life users (i.e. your care team).
        Salesforce does <em>not</em> view or mine your personal health
        information; it simply provides the database and software that we use to
        enter and retrieve your records. The data in the EHR is encrypted and
        access controlled. By using Salesforce, we leverage robust security and
        reliability for health data management. If you’d like more information
        about Salesforce’s role or data protection measures, we can provide our
        sub-processor list and relevant documentation upon request.
      </li>
      <li>
        <strong>Amazon Web Services (AWS) (Hosting and Infrastructure):</strong>
        Our platform’s infrastructure is largely hosted on Amazon Web Services,
        which provides the servers and data centres where certain personal data
        may be stored or transmitted. AWS is used for things like application
        servers, databases, and backup storage for the Virtual Primary Care
        service. We choose AWS data centres in regions with strong data
        protection (for example, in the EU or UK) to store your data. AWS acts
        as a data processor in that it handles data on our behalf, but AWS
        personnel do not access your data unless absolutely required for
        maintenance and under strict conditions. AWS has a robust security
        environment – data at rest in our AWS databases is encrypted (we use
        strong encryption like AES-256), and data in transit is encrypted
        (HTTPS/TLS). We also take advantage of AWS security features (firewalls,
        monitoring, etc.) to protect the servers. AWS, being a major cloud
        provider, has implemented Binding Corporate Rules and/or uses Standard
        Contractual Clauses to lawfully handle any data that might cross borders
        (though our configuration aims to keep it in-region). In summary, AWS
        helps us ensure high availability and security of the service. They are
        contractually committed to GDPR compliance, and they will not use your
        data for anything other than providing infrastructure services to us.
      </li>
      <li>
        <strong>AI Triage/Assistant Tools (e.g. “Heidi”):</strong> To enhance
        our service, we may incorporate AI-driven tools – for example, a symptom
        checker or triage assistant that can guide you to the right care or
        provide preliminary health recommendations. (Internally or informally,
        we might refer to our virtual assistant as “Heidi”.) These tools are
        meant to support you and our clinicians, never to replace professional
        judgment. If you choose to interact with such an AI assistant (for
        instance, by entering your symptoms into a symptom checker chat), the
        information you provide will be processed by the AI system to generate a
        suggestion (such as possible conditions or next steps). We will ensure
        that any AI tool we use is provided by a trusted partner or developed
        in-house with appropriate data safeguards. For example, we have
        partnered with providers like Healthily to power certain recommendation
        engines, and any data sent to their system is handled under strict
        agreements. We typically anonymise or minimize data for these tools –
        meaning, if possible, we might send just the symptom data without your
        name or identifiers attached, unless it’s integrated in a way that the
        AI is within our environment. In any case, the AI outputs are
        <em>
          not
        </em>
        final or binding. Our human clinicians review and make the actual
        decisions (see <em>Automated Decision-Making and AI</em> section below
        for more). The AI tool’s purpose is to improve efficiency (for example,
        helping you navigate to the right kind of clinician or giving our
        doctors a draft summary). Any data shared with an AI provider is treated
        as confidential and cannot be used by them for any purpose other than
        providing the service to us. We will disclose to you when you are
        interacting with an AI system and you can always choose to speak
        directly with a human. “Heidi” or any similar AI is under our
        instruction as a data processor, and we have done due diligence to
        ensure compliance with GDPR and medical ethics for its use.
      </li>
      <li>
        <strong>Service Providers and Sub-Processors:</strong> Spectrum.Life
        relies on a number of other trusted third-party service providers to
        help run the Virtual Primary Care service. These include IT and cloud
        service providers (like AWS and Salesforce mentioned above),
        communication tools (for example, an email/SMS service to send you
        appointment reminders), analytics tools, and customer support software.
        We only share with them the information that is necessary for their
        function. For instance, if we use an email service to send you a
        reminder, we provide your email address and name and the content of the
        reminder – they cannot see your health records. All service providers
        are bound by Data Processing Agreements, requiring them to keep your
        data secure and confidential. Some examples:
      </li>
      <ul>
        <li>
          <strong>Email/SMS Providers:</strong> We might use providers such as
          SendGrid (email) or Twilio (SMS) to send communications. They would
          handle contact data and message content under strict security.
        </li>
        <li>
          <strong>IT Support and Cloud Storage:</strong> We have IT support
          firms or cloud backup services for maintenance and backup of our
          systems. They might incidentally have access to data when performing
          their duties, but only under our instruction and after passing
          background checks and confidentiality commitments. Backups on cloud
          platforms are encrypted.
        </li>
        <li>
          <strong>Analytics/Diagnostic Tools:</strong> We may use tools like
          Google Analytics or error tracking software to gather anonymous
          information on system performance. These might set cookies
          (addressed below) and process usage data. We configure these tools
          to avoid collecting identifying info wherever possible (for example,
          we anonymise IP addresses for Google Analytics).
        </li>
        <li>
          <strong>Professional Advisors:</strong> Occasionally, we might share
          necessary data with our auditors, legal counsel, or insurance
          providers if needed (for example, during an audit or to get legal
          advice on a case). These recipients are also bound to
          confidentiality and legal obligations to protect your data.
        </li>
      </ul>
    </ul>
    <p>
      We maintain an up-to-date list of our major sub-processors (third parties
      processing data on our behalf) which we can provide to you upon request. In
      all cases, we ensure that any third party we share data with has a
      legitimate need for it and will handle it with the same care we do. We never
      allow third parties to use your data for their own marketing or purposes
      outside of the service we contract them for.
    </p>
    <ul>
      <li>
        <strong>Legal and Regulatory Disclosures:</strong> We may disclose your
        personal information to third parties when required by law or necessary
        to protect rights. For example, we might share information with:
      </li>
      <ul>
        <li>
          <strong>Regulatory Bodies or Medical Councils:</strong> If a
          healthcare regulator or data protection authority requires us to
          submit to an inspection or provide information, we will comply as
          needed (e.g., providing records to the DPC or ICO if they are
          investigating a complaint, or to a medical council if there’s a
          professional conduct review).
        </li>
        <li>
          <strong>Law Enforcement or Courts:</strong> If we receive a court
          order, subpoena, or lawful directive to disclose data, we will
          evaluate it with our legal team and comply if required. We will only
          provide what is legally mandated. If not obviously illegal or overly
          broad, we would also attempt to inform you, so you have a chance to
          object or seek legal protection, unless we are legally forbidden
          from doing so.
        </li>
        <li>
          <strong>Emergency Situations:</strong> As mentioned, if sharing data
          with emergency medical services or law enforcement will protect
          someone’s life or safety, we may do so under vital interests. For
          instance, providing your known medical conditions to paramedics.
        </li>
        <li>
          <strong>Enforcing Our Rights:</strong> If necessary, we might share
          data to enforce our terms of service or to defend against legal
          claims. For example, if there is a litigation or insurance claim
          relating to a service provided, relevant data may be shared with our
          lawyers, courts, or insurance investigators. This would be done
          under confidential conditions and only what’s necessary for the
          proceeding.
        </li>
      </ul>
    </ul>
    <p>
      In all instances of sharing, our principle is to share the minimum
      information necessary and to do so lawfully and transparently. We do
      <em>
        not
      </em>
      engage in any selling of personal data or unauthorised sharing for
      advertising or profiling by others. If you have questions about a specific
      third party and whether they have your data, feel free to contact us. We can
      clarify if and why your data might be shared with them. Remember, you have
      the right to know about our data transfers and to object in some cases (see
      <em>Your Rights</em>). We will honour those rights in our data sharing
      arrangements.
    </p>
    <p>
      <strong>International Data Transfers</strong>
    </p>
    <p>
      Spectrum.Life is based in Ireland, and we primarily store and process data
      within the European Economic Area (EEA) (which includes EU countries) and in
      the UK. We recognise that data protection laws in Europe and the UK are
      strict about personal data leaving those regions. We want to reassure you
      that we have designed the Virtual Primary Care service to keep your data
      within jurisdictions with strong data protection laws whenever possible.
    </p>
    <ul>
      <li>
        <strong>Data Location:</strong> As of now, all personal and health data
        for this service is stored on secure servers located in the EEA (for
        example, in EU data centres for Salesforce and AWS) and/or in the UK.
        Our custom-built EHR on Salesforce and the Signature RX prescription
        module are hosted within the EEA. This means that if you are in the UK,
        your data might technically reside on servers in Ireland or another EU
        country (which is permitted under UK law as the EU is deemed adequate),
        and vice versa EU users might have data in a UK server (the UK is
        considered adequate by the EU as well). In both cases, protections are
        equivalent or highly similar due to reciprocal adequacy decisions.
      </li>
      <li>
        <strong>No Routine Transfers Outside EU/UK:</strong> We do not routinely
        transfer your personal data to any country outside the EU/UK that does
        not have an adequate level of data protection. Our DPIA confirms that
        the new Primary Care service was <em>designed to avoid</em> such
        transfers. For instance, our cloud infrastructure is set up to use EU
        regions, and any third-party we use is either EU/UK based or has
        appropriate safeguards.
      </li>
      <li>
        <strong>Possible Transfers with Safeguards:</strong> If it ever becomes
        necessary to transfer your data outside the EU/UK (for example, if we
        engage a specialist tech provider in the United States for certain
        functionality, or if a non-EEA support team member needs temporary
        access for troubleshooting), we will ensure that proper legal safeguards
        are in place before such transfer happens. The safeguards we rely on may
        include:
      </li>
      <ul>
        <li>
          <strong>Standard Contractual Clauses (SCCs):</strong> These are
          European Commission-approved contracts that bind the recipient of
          the data (e.g., a U.S. service provider) to protect your data to EU
          standards. We have SCCs in place with any non-EEA processors when
          required. They also give you enforceable rights regarding your data.
          We also implement supplementary technical measures (like encryption)
          as needed alongside SCCs.
        </li>
        <li>
          <strong>Data Privacy Framework (DPF):</strong> If transferring to
          the U.S., we may use the EU-U.S. Data Privacy Framework mechanism if
          the recipient is certified under it. For example, if a U.S. vendor
          is self-certified under the DPF (the successor to Privacy Shield),
          your data can legally flow to them with that protection. Similarly,
          the UK has a “data bridge” extension of this framework for UK-US
          transfers. We keep track of which providers are certified and ensure
          they maintain that status.
        </li>
        <li>
          <strong>Binding Corporate Rules (BCRs):</strong> Some large tech
          companies we might use have approved BCRs for intra-company
          transfers (e.g., Salesforce or AWS might have BCRs). If applicable,
          those can cover transfers within that company’s global structure.
        </li>
        <li>
          <strong>Adequacy Decisions:</strong> If the data is going to a
          country that the European Commission (or UK government) has
          officially deemed “adequate” (meaning their laws offer comparable
          protection), then we can transfer on that basis. Examples include
          countries like Canada, Japan, New Zealand, etc. We will always
          prefer partners in adequate countries.
        </li>
        <li>
          <strong>Explicit Consent or Derogation:</strong> Only in rare cases,
          if none of the above safeguards are available for a necessary
          transfer, we might ask for your explicit consent for the transfer
          after informing you of any risks, or rely on another specific
          derogation under Article 49 GDPR (like if the transfer is necessary
          for the performance of our contract with you – for example, if you
          are traveling and want us to send your data to a doctor abroad at
          your request). This would be done strictly on a case-by-case basis.
        </li>
      </ul>
      <li>
        <strong>Transparency:</strong> We will be transparent with you about
        cross-border data flows. If we start using a new provider that stores
        data outside the EU/UK, we will update this Privacy Notice accordingly
        and notify users if appropriate. For now, at the time of this notice, no
        transfers outside the EU/UK are occurring for the delivery of Virtual
        Primary Care. All systems are hosted in jurisdictions with GDPR-level
        protection, which should give you peace of mind.
      </li>
      <li>
        <strong>Government Access:</strong> We are aware of concerns about
        government surveillance in some non-EU countries. By keeping data in the
        EU/UK, it is mainly subject to EU/UK law. If we ever had to transfer to,
        say, the U.S., and even with SCCs/DPF, we would assess the risk of
        government access and implement additional encryption if needed to
        mitigate it. We keep an eye on legal developments (for example, new SCC
        updates, court rulings like Schrems II, etc.) and will adapt our
        transfer mechanisms accordingly to ensure continuous compliance.
      </li>
    </ul>
    <p>
      In summary, we handle international data flows with great caution. Our
      default is to keep your data in Europe/UK. If there’s any need to go beyond,
      we will use legally approved safeguards and let you know. If you have
      questions about where your data is stored or transferred, please contact us.
      We can provide information about our current data hosting locations and
      copies of relevant safeguards (e.g., the SCCs) if needed. Your data
      protection rights travel with your data, so no matter where it goes, we
      ensure it remains protected.
    </p>
    <p>
      <strong>Data Retention and Deletion</strong>
    </p>
    <p>
      We retain your personal data only for as long as necessary to fulfill the
      purposes we collected it for, including meeting any legal, accounting, or
      reporting requirements. Healthcare data, in particular, is often subject to
      specific retention rules to ensure continuity of care and compliance with
      medical standards. Below, we outline how long we typically keep different
      types of data related to the Virtual Primary Care service and how we
      securely dispose of it when it’s no longer needed:
    </p>
    <ul>
      <li>
        <strong>Medical Records from Consultations:</strong> If you use our
        Virtual Primary Care service, we will create a medical record of each
        consultation (including the clinician’s notes, any diagnosis or outcome,
        and prescription info). These clinical records are generally retained
        for seven (7) years from the date of your last consultation with us.
        This retention period aligns with standard healthcare practices and
        legal requirements in many jurisdictions, where healthcare providers are
        expected to keep records for a minimum period (often 7 years for adult
        patients) for patient safety, continuity of care, and medico-legal
        purposes. Retaining your records for this period allows us to better
        treat you if you come back (we have your history), and it allows us to
        have documentation in case of any issues or follow-up. If services are
        provided to minors (under 18), the retention period may be longer –
        typically we might retain those records until the minor reaches’
        adulthood plus a certain number of years. For example, in some cases, a
        minor’s records might be kept until they turn 21 or 25, depending on
        local regulations, to ensure the records are available once they are
        adults. We follow local laws on this. After the retention period
        expires, the default action is that we will delete or permanently
        anonymise those medical records, unless a valid reason exists to keep
        them longer (see exceptions below).
      </li>
      <li>
        <strong>Account Information:</strong> Your basic account data (like your
        name, contact details, login credentials, and preferences) is kept for
        as long as your account is active with the Virtual Primary Care service.
        If you stop using the service or request your account to be deleted, we
        will delete or anonymise this information within a reasonable time after
        account closure. Typically, once you or your sponsoring organisation
        terminates access, we will first ensure all ongoing treatment or
        obligations are concluded, then proceed to delete personal data. We may
        retain some minimal info thereafter to recognize that you were
        previously an authorized user (for example, to avoid sending you
        re-invitation emails or to honour an opt-out). Also, if your account was
        linked to an employer/insurer contract that ends, we might archive your
        data and then remove it according to our retention schedule. Rest
        assured, we won’t keep your personally identifiable account data longer
        than necessary.
      </li>
      <li>
        <strong>Communication Records:</strong> If you contacted us via support
        email, chat, or phone, we may retain those communications for a limited
        period for quality assurance and to have a history of interactions.
        Generally, customer support tickets or emails are kept for a few years
        (often 2–3 years). We keep them that long in case you return with a
        follow-up question or if we need to review what was communicated (e.g.,
        if you had a complaint and it resurfaces). These communications usually
        include your name and correspondence. They are stored securely and only
        accessed if needed for support/reference. If you wish, you can request
        that we delete specific communications and if we have no ongoing need or
        legal requirement, we will do so.
      </li>
      <li>
        <strong>Prescription Data:</strong> Records of prescriptions (e.g., in
        the Signature RX module) will be retained in line with medical record
        retention since they are part of your healthcare history. We treat
        prescription logs as part of your clinical record, so they follow the
        7-year retention guideline as above. Signature RX as a processor also
        abides by similar retention rules – they won’t keep your data longer
        than needed either.
      </li>
      <li>
        <strong>Technical Logs and Analytics:</strong> Raw technical logs
        (server logs, app analytics events) that contain personal data (like IP
        addresses or user IDs) are generally kept for a shorter period, often
        just a few weeks or months, unless they need to be retained longer for
        security analysis. We frequently anonymise or aggregate analytics data
        and keep the non-identifiable aggregate results for longer (possibly
        indefinitely for trend analysis). For instance, we might delete detailed
        clickstream logs after 12 months but retain a summary like “monthly
        active users per month” without personal identifiers for year-over-year
        comparisons. Any personal elements in analytics are disposed of as soon
        as they’re no longer needed for immediate analysis.
      </li>
      <li>
        <strong>Backups:</strong> Our system creates routine encrypted backups
        of data for disaster recovery. These backups are stored securely (e.g.,
        on AWS) and are kept for a limited time (for example, rolling backups
        over 30 days, with some periodic longer-term backups). If we delete or
        anonymise data from our live systems, we will also ensure that the data
        is deleted from backups in due course. There might be a short lag
        because backups aren’t instantly purged (they typically expire or are
        overwritten). However, our policies ensure that no backup is retained
        beyond its useful retention period, and backups too will be fully
        deleted on schedule. We encrypt backups and restrict access, so even
        during their retention, they are safe. When a backup is retired, we
        securely delete it (for example, using secure wipe methods so it cannot
        be recovered).
      </li>
      <li>
        <strong>Legal Hold and Disputes:</strong> If we are involved in a legal
        dispute or are subject to a legal requirement to preserve data (a
        “litigation hold” or regulatory order), we will retain the relevant data
        for as long as instructed by the authority or as needed to protect our
        legal rights. During such a hold, we cannot delete the data even if it
        would otherwise meet our deletion criteria. But we will not use it for
        anything other than the legal matter at hand. Once the hold is lifted or
        the matter is resolved, if the data is no longer needed, we will
        promptly proceed with deletion or anonymisation.
      </li>
      <li>
        <strong>Deletion Process:</strong> When data reaches the end of its
        retention period or you validly request its deletion, we will erase it
        in a secure manner. We either permanently delete the records from our
        databases or anonymise them so that they can no longer be linked to you.
        “Anonymisation” means we strip out or irreversibly scramble personal
        identifiers, turning the data into statistical information that cannot
        be traced back to you (for example, turning a record into “Patient X,
        age 35, had consultation for headache” with no way to know who Patient X
        is). We use automated workflows where possible to manage retention – our
        systems are configured to flag or remove data that is past its retention
        date. Additionally, our data processing agreements with partners (like
        Signature RX, Salesforce, AWS) require them to delete data from their
        systems once we instruct them to, in line with our retention policies.
        We keep evidence of deletion requests and confirmations to ensure
        completion. Once data is deleted or anonymised, it’s gone and cannot be
        reconstructed (when anonymised, it no longer counts as personal data).
        If deletion is impossible in some systems (maybe because of backup
        archives or technical constraints), we will ensure it’s put “beyond use”
        – meaning we’ll secure it and isolate it from any further processing
        until it can be deleted.
      </li>
    </ul>
    <p>
      If you decide to stop using the Virtual Primary Care service and want all
      your personal data removed, you can contact us to request deletion (see <em>
      Your Rights</em> below). We will let you know if any data must be retained
      (e.g., “we cannot delete X record for legal reasons until date Y, but we
      will delete everything else now”). We aim to be as accommodating as possible
      while following the law.
    </p>
    <p>
      In summary, we keep your health data for the period it’s needed for your
      care and as required by law, and no longer. We have a schedule in place to
      review and securely dispose of data at the appropriate times. Our philosophy
      is not to keep personal data indefinitely “just because” – if there is no
      justification to hold it, we remove it. This helps protect your privacy in
      the long run.
    </p>
    <p>
      <strong>Cookies and Tracking Technologies</strong>
    </p>
    <p>
      Our Virtual Primary Care platform uses cookies and similar tracking
      technologies to ensure it functions properly and to enhance your user
      experience. This section explains what these technologies are, how we use
      them, and your choices regarding them. Please note that since this is a
      healthcare service, we primarily use cookies for operational purposes (not
      for advertising), but we still want you to be informed about their usage.
    </p>
    <p>
      <strong>What Are Cookies?</strong> <br />
      Cookies are small text files that a website or app stores on your device
      (computer, smartphone, etc.) when you visit or use it. They often include a
      unique identifier and some information. Cookies allow the website/app to
      remember your actions and preferences over time. For example, a cookie can
      keep you logged in by remembering your session ID, or remember your language
      preference so you don’t have to select it each time. Cookies can be
      “first-party” (set by our platform) or “third-party” (set by others, like
      analytics providers, when you use our service). Besides cookies, similar
      technologies include web beacons (tracking pixels), local storage, and SDKs
      in mobile apps – they function similarly by storing or retrieving
      information on a device. In this notice, we’ll just say “cookies” to refer
      to all these types of technologies for simplicity.
    </p>
    <p>
      <strong>How We Use Cookies:</strong> <br />
      Spectrum.Life uses cookies in the Virtual Primary Care portal for several
      reasons:
    </p>
    <ul>
      <li>
        <strong>Necessary Functions:</strong> Some cookies are essential for the
        service to work correctly. For instance, when you log into your account,
        a cookie keeps you authenticated as you navigate between pages, so our
        server knows the requests are from your authenticated session. Without
        this, you’d have to re-log in on every click, which isn’t feasible. We
        also use cookies to implement security measures – for example, to help
        detect suspicious logins or to enable features like load balancing
        (distributing traffic across servers) which improves stability. These
        cookies do not gather information about you for marketing or remember
        where you’ve been on the internet; they purely keep the core service
        running. Because they are strictly necessary, we do not require consent
        for these, and you cannot opt-out of them if you want to use the service
        (other than by not using the service, which obviously defeats the
        purpose). If you disable cookies entirely via your browser, our platform
        may not function properly – for example, you might not be able to log in
        or use some features.
      </li>
      <li>
        <strong>Preferences and Convenience:</strong> We may use cookies to
        remember your preferences and make the service more user-friendly. For
        example, if the platform has a language toggle or certain user interface
        settings (like text size, color scheme), a cookie might save that
        preference so that it’s applied each time you return. Another example is
        that after you dismiss a one-time message or tutorial, a cookie might
        note that, so we don’t show it to you again. These cookies are there to
        streamline your experience. They typically store no sensitive data –
        often just a yes/no or an ID referencing your settings.
      </li>
      <li>
        <strong>Analytics and Performance:</strong> We use cookies and similar
        trackers to collect information about how users interact with the
        Virtual Primary Care platform. This helps us improve the service. For
        instance, we might use Google Analytics or a similar tool to get
        aggregate stats on usage (e.g., how many users book appointments on
        mobile vs desktop, or which pages are taking longest to load). The
        information collected can include things like your device type, pages
        visited, time spent, and any errors encountered. We configure these
        analytics tools to respect privacy: where possible, we anonymise your IP
        address and do not send personally identifying information. These
        analytics cookies are <em>optional</em> – we will either seek your
        consent for them or run them under a legitimate interest basis with the
        opportunity for you to opt-out, depending on legal requirements. The
        data from analytics cookies is used exclusively to improve our digital
        service quality and troubleshoot issues. For example, if we see many
        users dropping off at a certain step, we know to investigate and
        possibly redesign that step. We do not use analytics to track individual
        users or to make decisions about your care. The data is aggregated and
        focuses on trends. If you prefer not to be included even in anonymised
        analytics, you can disable these cookies as described below.
      </li>
      <li>
        <strong>Marketing and Referral (Minimal Use):</strong> Generally, within
        the Virtual Primary Care portal itself, we do not display third-party
        ads or do cross-site advertising tracking – it’s a healthcare service,
        not an ad-supported website. However, if you arrived at our service via
        certain promotional campaigns or if we ever run informational promotions
        within the app (like “check out our new wellness webinar”), we might use
        a cookie to track the campaign’s effectiveness or to ensure you don’t
        see a promo more than once. Additionally, if our service is integrated
        with the broader Spectrum.Life platform, and you have consented to
        marketing across the platform, a cookie might be used to personalize
        content or offers shown to you. These scenarios are limited and would
        only happen with appropriate consent under ePrivacy/PECR rules. If we
        were to use any advertising cookies or trackers, we would explicitly ask
        your permission (for example, via a cookie banner or settings). As of
        now, the use of such cookies in this service is minimal to none; your
        main concern with cookies here should be around the functional and
        analytic ones described above.
      </li>
    </ul>
    <p>
      Third-Party Cookies: Some cookies on our platform might be set by
      third-party services that we use. For example:
    </p>
    <ul>
      <li>
        If we embed a video consultation widget provided by a partner, that
        partner might set a cookie for making the video call work (like WebRTC
        signalling).
      </li>
      <li>
        Our analytics provider sets cookies to distinguish users (e.g., Google
        Analytics’ _ga cookie).
      </li>
      <li>
        If we use a customer survey tool or chat support widget, that tool might
        use cookies.
      </li>
    </ul>
    <p>
      In all cases, we choose reputable third parties who will not misuse the
      cookie data, and we ensure they only set cookies for the intended purpose.
      We list the key third-party tools in our cookies policy (or can provide upon
      request), so you know who else might be involved.
    </p>
    <p>
      <strong>Cookie Choices and Controls:</strong> <br />
      When you first use our web portal, you will be presented with a notice about
      cookies. We will give you an option to allow or disallow optional cookies
      (like analytics) if required. You can always manage your cookie preferences
      through your browser settings as well. Most browsers allow you to view,
      delete, and block cookies. You can typically find these options under the
      “Privacy” or “Security” settings of your browser. For example, you can set
      your browser to block all third-party cookies or to clear cookies when you
      close it. Keep in mind, if you block all cookies, including necessary ones,
      our service might not function properly– for instance, you won’t be able to
      stay logged in. We recommend that you allow at least the essential cookies.
      But it’s your right to control cookies as you see fit. If you disable
      certain categories (like analytics), the site will still work, but we won’t
      include your visits in our metrics.
    </p>
    <p>
      If we have a cookie preferences manager in the app or site (where you can
      toggle categories on/off), you can use that at any time to change your
      settings. Also, you can use browser extensions or opt-out tools provided by
      some third parties (e.g., Google offers a Google Analytics Opt-out Browser
      Add-on). On mobile apps, you may control tracking via your device settings
      (for example, opting out of analytics in the app’s settings if available, or
      disabling ad tracking at the device level).
    </p>
    <p>
      <strong>Similar Technologies:</strong> If we use any non-cookie tracking
      (like pixels in emails to see if you opened them, or local storage for
      caching data), we treat those with the same level of care. For instance, our
      service emails might contain a tiny image pixel that, when loaded, tells us
      you opened the email – we use that mainly to gauge engagement, so we don’t
      spam you with unwanted messages. You can disable image loading in your email
      if you don’t even want that. But note, critical service emails (like
      appointment details) we assume you will open; we don’t use that info for
      anything beyond ensuring communication effectiveness.
    </p>
    <p>
      <strong>Do-Not-Track and Global Privacy Controls:</strong> Currently, our
      Virtual Primary Care service does not react to “Do Not Track” signals in a
      special way – we treat all users the same, but we already minimize tracking.
      If you have Global Privacy Control (GPC) enabled, that typically is more
      relevant for stopping sale/sharing of data for targeted ads, which we don’t
      do anyway. We honour privacy by design by not sharing your data for such
      purposes. So, while we might not have a specific technical response to DNT,
      effectively we align with its intent by not engaging in cross-site tracking.
    </p>
    <p>
      In summary, our use of cookies is mainly to ensure a smooth and secure
      health service experience. We don’t use them to invade your privacy. We want
      you to feel comfortable that nothing about the cookie usage compromises the
      confidentiality of your health interactions. If you’d like more details on
      specific cookies we use, please ask us for a list – we can provide names of
      cookies and their functions. We’re committed to transparency on this front
      as well.
    </p>
    <p>
      <strong>Data Security and Breach Notification</strong>
    </p>
    <p>
      We understand that the security of your personal data – especially health
      data – is paramount. Spectrum.Life has implemented robust technical and
      organisational measures to safeguard your information from unauthorised
      access, alteration, disclosure, or destruction. We continuously improve our
      security practices in line with industry standards and regulatory
      requirements (including the heightened expectations set by laws like the EU
      NIS2 Directive for cybersecurity). In this section, we outline how we
      protect your data and what we would do in the event of a security incident.
    </p>
    <p>
      <strong>Technical Security Measures:</strong> We employ state-of-the-art
      technologies to protect data both in transit and at rest. When you use the
      Virtual Primary Care portal, all communication between your device and our
      servers is encrypted using HTTPS/TLS. This means any personal data (from
      login credentials to video call data to health info) is transmitted securely
      and cannot be easily intercepted. Likewise, sensitive data at rest (stored
      on our servers or databases) is encrypted – we use strong encryption
      protocols (like AES-256) for databases and files, so even if someone got
      physical access to the storage, they couldn’t read the data without the
      encryption keys. Our systems require authentication for access, and we
      enforce multi-factor authentication for our administrators and any staff
      with elevated privileges, adding an extra layer against unauthorised logins.
      We maintain up-to-date firewalls and intrusion detection systems to guard
      our network perimeter, and we regularly apply security patches to our
      software and infrastructure to fix vulnerabilities. Additionally, we use
      monitoring tools to keep an eye on suspicious activities and will act
      swiftly if an anomaly (like multiple failed login attempts or unusual data
      access patterns) is detected.
    </p>
    <p>
      <strong>Organisational and Access Controls:</strong> Internally, we follow
      the principle of least privilege – staff and contractors can only access the
      data necessary for their role, and nothing more. For example, a doctor can
      view your medical records for treatment purposes, but a customer support
      agent cannot see your consultation notes (unless necessary for a specific
      support task and even then likely under supervision). An IT engineer might
      have access to system logs and the technical backend, but not to the
      contents of medical records unless it’s absolutely required for debugging
      and with approval. We keep access logs to trace who accessed what data and
      when, which helps enforce accountability. All Spectrum.Life employees and
      contractors who handle personal data are under confidentiality agreements
      and receive training on data protection and privacy. We perform background
      checks where appropriate (especially for roles with access to sensitive
      data) and ensure our teams are trustworthy. Physical security is also
      addressed: our offices have controlled access, and our data center providers
      (like AWS) have stringent physical security (guarded facilities, biometric
      access controls, surveillance, etc.). Although we rely on cloud
      infrastructure, we treat those cloud environments as an extension of our
      responsibility – we ensure configurations are secure (no leaving ports open
      or data buckets public, for example).
    </p>
    <p>
      <strong>Compliance and Standards:</strong> Spectrum.Life’s information
      security management aligns with well-known frameworks. We draw on standards
      like ISO/IEC 27001 (information security management system standard) and the
      IASME/Cyber Essentials framework in the UK. We incorporate the best
      practices from these standards: things like risk assessment, incident
      response planning, continuous monitoring, and improvement. We maintain
      up-to-date security and data protection policies that our staff must follow,
      covering things like how data is handled, how incidents are reported, and
      how access is granted/revoked. Our commitment to these standards means we
      regularly review our security posture to ensure it meets the evolving
      threats and regulatory demands.
    </p>
    <p>
      <strong>Testing and Auditing:</strong> To make sure our security controls
      are effective, we perform regular testing. This includes vulnerability
      scanning of our systems, penetration testing by independent experts
      (simulated hacking attempts to find weaknesses), and internal audits of
      processes. If these tests uncover any issues, we take prompt action to
      address them. We also evaluate our critical third-party providers for their
      security measures – for example, we review AWS’s and Salesforce’s security
      documentation and certifications, and we may require security questionnaires
      or audits for any new vendor who will handle personal data. Furthermore,
      some of our enterprise clients (like large corporate customers) might
      conduct their own security assessments of our service. We cooperate with
      such audits and treat any findings with the utmost seriousness. Through
      continuous testing and third-party scrutiny, we aim to ensure that
      weaknesses are identified and fixed proactively.
    </p>
    <p>
      <strong>Incident Response and Breach Notification:</strong> Despite all
      precautions, no system is 100% immune to incidents. That’s why we have a
      detailed incident response plan in place. If a security incident or data
      breach is suspected, we activate this plan immediately. It involves
      containing the issue (stopping any further unauthorised access or damage),
      investigating the extent of the incident (what happened, whose data is
      affected, how, etc.), and remedying the vulnerability or cause. We have
      designated an incident response team that includes our DPO, IT security
      personnel, and relevant managers.
    </p>
    <p>
      If a personal data breach does occur (meaning a security incident leading to
      accidental or unlawful destruction, loss, alteration, unauthorised
      disclosure of, or access to personal data), we will quickly assess the risk
      it poses to your rights and freedoms. In line with GDPR, if the breach is
      likely to result in a risk to you (e.g., could lead to identity theft, harm,
      or inconvenience), we will notify the appropriate supervisory authority (DPC
      in Ireland, or ICO in the UK, depending on jurisdiction) without undue delay
      and within 72 hours of becoming aware of it, unless a delay is justified. If
      that 72-hour deadline is exceeded, we will explain the reasons. Our report
      to the authority would include information on what happened, what data is
      involved, and what we’re doing about it.
    </p>
    <p>
      Moreover, if the breach is likely to result in a high risk to you (for
      example, if sensitive health data was exposed and could cause significant
      harm or embarrassment), we will also communicate the breach to you directly
      and without undue delay. We’ll tell you in plain language what happened,
      what data is affected, what the implications are, and what steps we
      recommend you take to protect yourself (if any). We’ll also let you know
      what we are doing to mitigate the impact and prevent future incidents. We
      believe in being upfront – we won’t hide breaches. Our aim in such
      communication is to help you take any necessary actions (for instance,
      change passwords, monitor accounts, etc., depending on the situation) and to
      maintain your trust by being honest about issues.
    </p>
    <p>
      We document all incidents, even minor ones, and our responses to them. This
      helps us learn and improve our safeguards. If needed, we also will
      coordinate with law enforcement or cyber security experts to assist in the
      investigation and response.
    </p>
    <p>
      <strong>Continuous Improvement:</strong> The security landscape is
      constantly evolving. New threats and vulnerabilities emerge all the time. We
      keep ourselves updated on the latest security news, threat intelligence, and
      guidance from authorities. For example, if there are new recommended
      encryption standards or patches for a critical vulnerability (like something
      on the scale of Heartbleed or Log4j vulnerabilities), we act promptly to
      implement them. We also ensure our staff receives regular training on
      security awareness and data protection best practices (at least annually, if
      not more often). This training covers things like phishing email
      recognition, secure data handling, and incident reporting procedures. A
      culture of security is encouraged internally – everyone has a role in
      protecting data. Additionally, we carry cyber insurance as a safety net,
      which can provide resources for response and mitigation in the event of a
      major incident. However, our primary focus is on prevention and
      preparedness.
    </p>
    <p>
      We also encourage you to take steps to keep your account secure. Choose a
      strong, unique password for your Spectrum.Life account and do not share it
      with others. We will never ask you for your password via email or phone – be
      wary of any phishing attempts pretending to be us. If you receive suspicious
      communications or notice anything odd (like unrecognised devices on your
      account), please notify us immediately. We’ll work with you to secure your
      account.
    </p>
    <p>
      In summary, we employ rigorous security measures to protect your data and
      have clear plans for handling any issues that arise. Our goal is to provide
      you a secure service so you can focus on your health without worry. If you’d
      like more detailed information about our security (to the extent we can
      share without compromising it), feel free to contact us. We can, for
      instance, discuss our encryption practices or authentication policies in
      more detail if needed.
    </p>
    <p>
      <strong>Children’s Privacy</strong>
    </p>
    <p>
      Our Virtual Primary Care service is generally designed for adult users
      (typically employees or insurance members). In most cases, minors (children
      under 18) are not direct users of our platform, except possibly in specific
      scenarios such as: a parent scheduling a consultation for a child, a family
      insurance plan that covers dependents, or a student assistance context for
      older teenagers. We recognise the importance of protecting children’s
      personal data and complying with laws regarding children’s privacy.
    </p>
    <p>
      If you are under the age of 16 (or under 13 in the UK): Please ensure that
      you have permission from your parent or legal guardian before using our
      Virtual Primary Care service. We do not knowingly offer online account
      registration to children below the applicable digital consent age without
      parental authorization. For example, in the UK, the minimum age to consent
      to online services is 13, and in many EU countries (including Ireland) it’s
      16, unless lowered by national law. So, if you fall into those younger age
      groups, a parent/guardian should be involved in your use of the service.
    </p>
    <p>
      <strong>Parental Consent and Involvement:</strong> If a child’s personal
      data will be processed in the course of providing the service, we will seek
      parental or guardian consent as required by law. For instance, if an
      employer offers our service to employees’ children, we will likely have the
      parent serve as the primary account holder or provide explicit consent for
      the child’s data to be used. We will also make sure that communications and
      explanations are directed appropriately so that both the minor (if old
      enough to understand) and the parent/guardian are informed.
    </p>
    <p>
      <strong>Use of Data for Children:</strong> Any data about a child patient
      that we collect (such as medical information during a consultation for a
      minor) is used strictly for the purposes of providing care to that child and
      for safety. We treat children’s health records with the highest
      confidentiality. Only clinicians and staff with a need-to-know will access
      them (as with adult records). We do not use children’s data for marketing or
      profiling. We do not disclose a child’s information to school officials or
      employers, etc., unless it’s a very specific situation like a school being
      the sponsor and even then, only aggregated info or as required for duty of
      care. Essentially, we give children’s data the same or greater protection as
      adults.
    </p>
    <p>
      <strong>Services Aimed at Children:</strong> Our platform is not aimed at
      young children. We do not provide features that would be of interest to
      children without parental oversight. If in the future we develop a
      teen-friendly health education feature or similar, we will ensure it
      complies with relevant age-appropriate design/code guidelines (like the
      ICO’s Age-Appropriate Design Code).
    </p>
    <p>
      If a Child Provides Us Data Without Consent: If we discover that we have
      collected personal data from a child under the relevant age of consent
      without proper parental consent, we will delete that data as soon as
      possible. For example, if a 12-year-old somehow signed up pretending to be
      older and we later learn of it, we will remove their account and data unless
      a guardian quickly provides consent for continued use.
    </p>
    <p>
      <strong>Parental Rights:</strong> If you are a parent or guardian and you
      have questions about your child’s use of our service or wish to exercise the
      child’s data subject rights on their behalf, please contact us. We may need
      to verify your identity and relationship to the child before fulfilling
      requests, to ensure we don’t give data to an unauthorised person. Typically,
      parents have the right to access and control personal data for children
      under their care (bearing in mind any medical confidentiality considerations
      as the child matures – healthcare providers may, in certain jurisdictions,
      respect the privacy of a minor in receiving health services like mental
      health support if the minor is deemed mature enough, all within legal
      allowances). We will handle these situations delicately and in line with
      applicable health and privacy laws.
    </p>
    <p>
      In summary, our service is not directed at young children, and we aim to
      limit processing of children’s data. Where we do include minors (such as
      older teenagers or dependents), we do so with proper consent and protection.
      If you are an organisation considering rolling out this service to families
      or schools, we can work with you on a separate children’s privacy addendum
      to ensure all responsibilities are clear.
    </p>
    <p>
      <strong>Automated Decision-Making and AI Tools</strong>
    </p>
    <p>
      Spectrum.Life’s approach to care is human-centric. We do not make any
      decisions about your healthcare or eligibility for services solely by
      automated means if those decisions could significantly affect you. In other
      words, we do not have any fully automated decision-making (as defined under
      GDPR Article 22) in our Virtual Primary Care service that would result in,
      say, a refusal of service or a clinical diagnosis without human involvement.
      However, we do leverage technology including artificial intelligence (AI)
      and algorithms as supportive tools. This section explains how we use AI or
      automated systems and ensures that human oversight is always present for
      important outcomes.
    </p>
    <p>
      <strong>AI Support in Healthcare (Triage and Recommendations):</strong> We
      continuously explore ways to enhance our services, and this may include AI
      or machine-learning tools to assist in providing care. For example, we might
      use an AI-powered symptom checker where you can input your symptoms and
      receive a preliminary suggestion or risk assessment. We might also use
      algorithmic sorting to match you with the most suitable clinician (based on
      your needs and the clinicians’ specialties) or to prioritise urgent cases.
      Additionally, our partnership with Healthily integrates an AI “Next Steps”
      recommendation engine that can give you guidance on self-care or whether you
      should see a doctor. These tools are intended to empower you and make the
      service more efficient, not to deny care or make final decisions. Any
      suggestion an AI gives (for instance, “It looks like you might have X, you
      should consult a doctor within 24 hours”) is purely informative and will be
      confirmed and validated by a human professional. In practice, the AI might
      help our nurses and doctors by flagging certain patterns or by drafting a
      potential diagnosis, but the clinician will review the information, exercise
      their professional judgement, and provide the actual advice or decision. We
      see AI as a <em>supportive assistant</em> that can handle routine tasks or
      provide data insights, thereby allowing humans to focus on complex, personal
      aspects of care.
    </p>
    <p>
      <strong>No Autonomous Medical Decisions:</strong> We do not have an AI that
      can prescribe medication or diagnose you on its own. For instance, even if
      an AI triage system suggests a particular treatment, no prescription will be
      issued unless a licensed human clinician agrees and authorises it.
      Similarly, if you interact with a chatbot for general advice, any serious
      recommendation (like a referral or a diagnosis) will either be generated
      from a predefined script or database or will instruct you to consult a
      human. There is always a human in the loop for decisions with legal or
      significant impact on you. We confirm that currently, Spectrum.Life does not
      engage in any profiling or automated processing that produces legal effects
      or similarly significant effects on individuals without human review. If
      that ever changes in the future, we will update this notice and implement
      required safeguards (like obtaining your consent or ensuring you have an
      appeal mechanism).
    </p>
    <p>
      <strong>Examples of AI Use:</strong> To make it concrete, here are some ways
      we might use AI and how we ensure human oversight:
    </p>
    <ul>
      <li>
        <strong><em>Symptom Checker Chatbot (“Heidi”):</em></strong> You type in
        your symptoms and answer some questions from the chatbot. The system
        algorithms compare your inputs to medical databases (possibly using
        Natural Language Processing) and give you a preliminary analysis, like
        “Your symptoms could indicate a migraine. It’s usually okay to manage at
        home, but since you mentioned severe pain, we advise speaking to a
        doctor.” This advice is general and based on population data. We ensure
        that the chatbot makes it clear it’s not a final medical verdict. If it
        ever says “speak to a doctor,” our system can facilitate that by
        connecting you to book an appointment. The actual diagnosis is then made
        by the doctor during your consultation.
      </li>
      <li>
        <strong><em>Risk Flagging:</em></strong> Our system might automatically
        flag if a combination of your symptoms indicates a possibly urgent
        issue. For example, chest pain and shortness of breath could be flagged
        as high priority so that you get seen quickly. This flagging might be
        done by an algorithm. However, it won’t automatically call an ambulance
        or anything extreme without human input. Our triage nurses or on-call
        doctors would see the flag and then take appropriate action (they make
        the call whether to treat it as an emergency).
      </li>
      <li>
        <strong><em>Personalized Content:</em></strong> Suppose the platform has
        a library of health articles. An AI might analyse what conditions you’ve
        had consultations for and suggest relevant educational content (e.g.,
        after a consultation for diabetes, it might suggest an article on diet
        management). This is a form of profiling to personalize your experience.
        It’s benign and intended to help you, and it doesn’t have a significant
        effect on you beyond providing helpful tips. You can always ignore the
        suggestions. We find this akin to a recommendation engine on a streaming
        service – it’s there for convenience, but not consequential.
      </li>
      <li>
        <strong><em>AI Medical Scribe:</em></strong> We might use an AI tool
        that listens (with your permission) to your consultation and generates a
        draft consultation note for the doctor. This saves time on
        documentation. The doctor will then edit and approve that note. The AI
        is not deciding what to put in your record on its own; it’s under the
        doctor’s control.
      </li>
    </ul>
    <p>
      <strong>Human Oversight (“Human in the Loop”):</strong> We emphasize that
      for any important decisions or evaluations, a human professional is involved
      and responsible. We value the nuance, empathy, and expertise that our
      doctors and nurses bring. AI can miss context or have biases, whereas a
      human can interpret tone, understand personal circumstances, and ensure
      compassionate care. For instance, if in the future we introduced an
      AI-driven health risk scoring, any high-risk result would be reviewed by a
      clinician who would then reach out to you or formulate a plan, rather than
      an automated message just telling you alarming news. We have internal
      protocols that anytime an AI flags something critical (like “this person
      might be suicidal” from an AI sentiment analysis), a human is alerted and
      intervenes appropriately – the AI would <em>never</em> send an automated
      “diagnosis” or action without a person. We effectively use AI as a second
      pair of eyes or a preliminary assistant, not as the final arbiter.
    </p>
    <p>
      <strong>Transparency and Your Control:</strong> We strive to be transparent
      about where and how we use AI. If you are interacting with an AI (like a
      chatbot), we will make it known (the chatbot may introduce itself as
      automated). You always have the option to bypass or opt out of AI
      interactions – for example, you can directly request to speak to a human
      clinician at any point. We also allow our clinicians to override AI
      suggestions. If an AI recommended a certain referral but the doctor
      disagrees based on their judgement, the doctor’s decision prevails. We also
      keep our AI tools compliant with emerging regulations (like the upcoming EU
      AI Act). We assess the risk level of any AI we deploy (for example, a
      diagnostic support tool would be considered high-risk AI in healthcare, so
      we ensure rigorous testing and oversight on it). We also ensure explanation:
      if an AI provides a recommendation, we aim to have it provide reasons (or
      our clinicians will explain the rationale to you). And importantly, you have
      rights related to automated processing: as mentioned in <em>Your Rights</em>
      , you have the right not to be subject to decisions based solely on
      automated means if they have significant effects. While we currently don’t
      do that, we still uphold your right. If you ever feel an outcome was too
      automated or not sufficiently reviewed by a human, let us know – we will
      have a human review the situation and you can contest the result. We include
      this commitment even though our processes already involve humans, to affirm
      that we take your concerns seriously.
    </p>
    <p>
      <strong>No Sale or Unapproved Profiling:</strong> We also clarify that we do
      not sell your data to any third parties for their own AI or profiling use.
      Any profiling we do is internal and aimed at improving your service (like
      matching you to the right program or content). We’re not feeding your data
      into some external AI systems without proper agreements and purposes tied to
      your care. For instance, we wouldn’t suddenly send your data to an insurance
      AI to assess premiums – nothing of that sort happens.
    </p>
    <p>
      In summary, AI in our service is a helper, not a decision-maker. We use it
      to enhance efficiency and quality, but always with human oversight,
      especially for clinical and significant decisions. We stay abreast of
      technological and regulatory developments to ensure our use of AI remains
      ethical, transparent, and compliant. Our goal is that you benefit from
      innovation (faster service, smart insights) without losing the personal,
      human touch that healthcare demands. If you have any questions or concerns
      about AI usage in our platform, or if you want to opt out of certain
      AI-driven features, please contact us – we will accommodate your preferences
      where possible.
    </p>
    <p>
      <strong>Your Rights as a Data Subject</strong>
    </p>
    <p>
      As someone whose personal data we process, you have a number of important
      rights under GDPR and UK data protection law. We are committed to enabling
      you to exercise these rights and to being transparent about how your data is
      used. Below, we outline your key data subject rights and explain what they
      mean and how you can use them. Don’t hesitate to reach out to us (via the
      contact info in the “Who We Are” section) if you wish to exercise any of
      these rights or have questions. These rights are provided free of charge,
      and we will respond within the legally mandated timeframes (usually one
      month, with the possibility of extension in certain cases).
    </p>
    <ul>
      <li>
        <strong>Right to Be Informed:</strong> You have the right to be informed
        about how we collect and use your personal data. This Privacy Notice
        itself is a primary way we fulfil this right– by giving you details on
        what data we collect, for what purposes, who we share it with, how long
        we keep it, etc. We aim to write it in clear language. If anything in
        this notice or in our data practices is unclear to you, you can ask us,
        and we will provide further information. Essentially, you are entitled
        to transparency, and we strive to provide that proactively.
      </li>
      <li>
        <strong>Right of Access:</strong> You have the right to access personal
        data that we hold about you. This means you can ask us to confirm if we
        are processing your data, and if so, request a copy of that data. This
        is commonly known as a “Subject Access Request.” You can request, for
        example, “Please send me a copy of all my health records and personal
        data that you have.” We will provide you with a copy of the information
        in a commonly used format (usually electronic, like PDF or DOC files,
        unless you request otherwise). Along with the data, we will also give
        you supplementary information as required – such as the purposes of
        processing, the categories of data, the recipients (or categories of
        recipients) with whom it’s shared, the retention periods, and the source
        of the data if it wasn’t collected from you. Typically, we will respond
        to access requests within one month of verifying your identity. If a
        request is complex or numerous, we may extend by up to two further
        months, but we’ll inform you if that’s the case. Access requests are
        generally free; we would only charge a fee if the request is excessive
        or repetitive, and even then it would be a reasonable amount (we’ve
        never had to do this).
      </li>
      <li>
        <strong>Right to Rectification:</strong> You have the right to have
        inaccurate personal data corrected and incomplete data completed. If you
        see any error in the data we have about you – whether it’s a misspelled
        name, an outdated phone number, or an incorrect piece of medical info –
        please let us know. We will rectify it promptly. For basic contact
        details, you might be able to log into your account settings and update
        them yourself; we encourage you to keep your profile up to date. For
        changes that require verification (like a legal name change), we might
        ask for documentation just to ensure accuracy, but we try to make the
        process as simple as possible. Correcting your data not only is your
        right but also helps us provide you better service (for example,
        reaching you at the right email or making sure your date of birth is
        correct in medical contexts). We will also communicate the correction to
        any third parties who received the incorrect data if necessary.
      </li>
      <li>
        <strong>Right to Erasure (Right to be Forgotten):</strong> You have the
        right to request the deletion of your personal data in certain
        circumstances. This right is not absolute, but you can ask us to erase
        data if: (a) it’s no longer necessary for the purpose we collected it
        (e.g., you’ve left the company and it’s been some time, and there’s no
        reason for us to keep your info); (b) you originally consented to a use
        of data and now withdraw that consent, and we have no other lawful basis
        to keep it; (c) you object to processing based on legitimate interests
        and we have no overriding grounds to continue; (d) we processed your
        data unlawfully; or (e) a law requires erasure. For instance, if you
        close your account and request deletion, in most cases we will be able
        to delete all personal data we hold about you (once we’ve settled any
        pending obligations). However, note that this right has exceptions – we
        might not delete data we are required to keep by law or that is
        necessary for legal claims or certain other limited reasons. For
        example, even if you request deletion, we may need to retain your
        consultation records for the 7-year medical retention requirement. In
        such cases, we will inform you about the specific data we must retain
        and why (e.g., “We cannot delete X record because it’s needed for legal
        compliance, but we will keep it only for that purpose and for the
        required period”). We will honour your erasure request to the fullest
        extent possible, and will erase all other data that isn’t under an
        exemption. Also, if your data has been made public by us (not typical in
        our context, but say you posted something on a forum), we will take
        reasonable steps to inform other controllers/processors to comply with
        your deletion request.
      </li>
      <li>
        <strong>Right to Restrict Processing:</strong> You can ask us to “pause”
        the processing of your personal data in certain situations. This means
        we would still store your data but temporarily refrain from using it or
        sharing it until the restriction is lifted. You can request restriction
        if: (a) you contest the accuracy of the data (we then restrict until we
        verify or correct it); (b) the processing is unlawful but you don’t want
        full deletion (perhaps you want us to keep the data for legal reasons
        but not process it); (c) we no longer need the data, but you need it
        kept for a legal claim; or (d) you have objected to our processing (see
        next right) and we’re considering whether our grounds override yours. If
        you exercise this right, we will mark the data as restricted and only
        process it for certain things – e.g., with your consent, for legal
        claims, to protect someone’s rights, or for important public interest
        reasons. We will inform you before lifting a restriction. For example,
        if you dispute the accuracy of your address and ask for restriction, we
        won’t use or change that address until it’s resolved. Once we verify it,
        we’ll either update it and resume normal processing (and let you know),
        or if it stays in dispute, we might keep it restricted. We will also
        make sure to convey any restrictions to third parties who have your
        data, if appropriate, so they don’t continue processing it either.
      </li>
      <li>
        <strong>Right to Data Portability:</strong> You have the right, in
        certain conditions, to receive the personal data you have provided to us
        in a structured, commonly used, machine-readable format, and to have
        that data transmitted to another controller at your request. This right
        applies to data that you actively provided or that was observed from
        your activities (like device data or service usage history), if we
        processed it by automated means and the basis for processing was either
        your consent or a contract with you. In practical terms for our service:
        if you request, we could export things like your account details, your
        consultation history, and so forth, in a format like CSV or JSON that
        could be understood by another system. You might want this if you were
        switching to a different provider and wished to bring your data over. We
        can also, if technically feasible, directly transmit the data to another
        provider on your behalf (e.g., sending your health record to a new
        healthcare provider’s system), but often it’s simpler to give it to you
        to pass on. Note that this right doesn’t apply to data that we generate
        through analysis or inference (like internal notes or risk scores), and
        it doesn’t apply when processing is based on legal obligation or
        legitimate interests. But where it does apply, we will comply. For
        example, you can ask for a portable copy of all your messages and
        records that <em>you</em> provided or that were observed from your
        usage. We aim to make this process smooth if you need it.
      </li>
      <li>
        <strong>Right to Object:</strong> You have the right to object to
        certain types of processing of your personal data, on grounds relating
        to your particular situation. Specifically, you can object at any time
        to processing that we do under legitimate interests or public
        interest/exercise of official authority (we don’t do the latter, so
        mainly legitimate interests would apply), and if you do, we must stop
        that processing unless we can demonstrate compelling legitimate grounds
        that override your interests, rights, and freedoms or if we need to
        continue processing for the establishment, exercise, or defence of legal
        claims. For example, if we process some of your data because we have a
        legitimate interest in doing so (like using your email to send
        satisfaction surveys), and you object, we will stop doing that unless we
        believe our reasons are stronger. In most cases, for things like direct
        marketing, your objection is absolute – which brings us to: you have an
        absolute right to object to any processing of your data for direct
        marketing purposes at any time. If we were sending out promotional
        emails about new services, you can opt out or object, and we will cease
        immediately – no questions asked. Also, if we were doing any profiling
        for marketing or other purposes, you can object to that too and we will
        stop. If you do object, we will carefully consider your objection. If,
        for instance, you object to processing of your data that we justify by
        legitimate interests (like internal analytics), we will evaluate whether
        there is a compelling need to continue (often, we might just stop for
        your data specifically if it’s not something that affects the service).
        We will then inform you of our decision and reasoning.
      </li>
      <li>
        <strong>
          Rights Related to Automated Decision-Making and Profiling:
        </strong>
        As discussed in the previous section, you have the right not to be
        subject to a decision based solely on automated processing, including
        profiling, that produces legal effects or similarly significant effects
        for you, unless an exception applies (like it’s necessary for a
        contract, authorised by law, or based on your explicit consent, and even
        then with safeguards). We have stated that our current services do not
        involve such automated decisions – we always involve humans for
        significant decisions. But we still mention your right here for
        completeness and to emphasise our stance on meaningful human review. If
        you ever feel that you were subject to an unfair automated decision, you
        can request human intervention, an explanation of the decision, and
        contest it. For example, if hypothetically our system ever auto triaged
        you as “low priority” and you think that’s wrong, you can ask for a
        human to review and override. We uphold this right fully, even though we
        design our processes to avoid triggering this scenario in the first
        place.
      </li>
      <li>
        <strong>Right to Withdraw Consent:</strong> Where we rely on your
        consent to process data, you have the right to withdraw that consent at
        any time. This will not affect the lawfulness of any processing we did
        based on consent before you withdrew it – in other words, past
        processing was okay but going forward we’ll stop. For example, if you
        gave consent for us to collect some wellness data or to receive our
        newsletter, you could later withdraw it and we will cease those
        activities. It’s easy to withdraw consent – for non-essential things
        like marketing, you can click “unsubscribe” in an email or toggle off a
        setting in your profile. For more sensitive consents (like consent for
        processing health data if we asked during registration), you can contact
        us to withdraw. We will then stop the processing that relied on consent.
        However, please note if the service depends on that processing, we might
        have to inform you of consequences. For example, if you withdraw consent
        for us to process your health data at all, we obviously cannot continue
        providing medical services, so we may have to deactivate your account.
        We will explain any such implications to you at the time, so you can
        make an informed decision. Withdrawing consent does not cost anything
        and can be done in the same manner as consent was given (or easier). If
        you ever need help withdrawing consent (say you can’t find where to
        unsubscribe), just contact us and we’ll assist.
      </li>
      <li>
        <strong>Right to Lodge a Complaint:</strong> If you believe we have
        infringed your data protection rights or handled your personal data in a
        way that is not lawful, you have the right to file a complaint with a
        supervisory authority. In the EU, you can choose to complain to the data
        protection authority in the country where you live, where you work, or
        where the issue occurred. Spectrum.Life’s lead authority is the Irish
        Data Protection Commission (DPC) since our HQ is in Ireland. In the UK,
        the relevant authority is the Information Commissioner’s Office (ICO).
        We have provided links to their websites earlier (dataprotection.ie and
        ico.org.uk). You do not need to go through us to file a complaint – you
        can go directly to them. That said, we genuinely welcome you to contact
        us first to try to resolve any concern. We are committed to addressing
        issues and often a direct conversation can resolve misunderstandings or
        allow us to fix a problem quickly. We will do our utmost to solve the
        matter amicably. But ultimately, it’s your right to seek help from the
        regulators, and we fully respect that. We will cooperate with any
        investigation or inquiry they undertake. Also, you have the right to an
        effective judicial remedy if you consider your rights have been violated
        – meaning you could take legal action against us or a processor if you
        wished. We hope that never becomes necessary, as we strive to follow the
        law closely.
      </li>
    </ul>
    <p>
      These rights are mostly free to exercise. In very rare cases (manifestly
      unfounded or excessive requests), we might charge a reasonable fee or
      refuse, but we will explain why if that situation arises. We have never had
      to refuse a legitimate request. We want to emphasize that we do not
      retaliate or limit your service for exercising your rights – you will
      continue to get the same quality of service after, say, requesting a copy of
      your data or correcting something. Your rights are integral to our service
      values, not an inconvenience.
    </p>
    <p>
      <strong>How to Exercise Your Rights:</strong> To exercise any of the above
      rights, you can contact us via:
    </p>
    <ul>
      <li>
        Email: The quickest way is to email our DPO at
        gdprspectrumlife@spectrum.life. Please state which right you wish to
        exercise (e.g., “Right of Access – please provide my data…” or “Right to
        Erasure – please delete X data…”) and any details that will help us
        fulfil your request (like the timeframe of data you’re interested in).
        We may need to verify your identity to ensure we don’t give your data to
        the wrong person – typically, if you’re emailing from the address we
        have on file and you’re logged in, that’s sufficient, but we might ask a
        couple security questions or for ID if there’s doubt. This is purely to
        protect you. Once verified, we’ll proceed with the request and keep you
        updated.
      </li>
      <li>
        <strong>Postal Mail:</strong> You can send us a written request to the
        DPO’s postal address given above. Please include your name, contact
        information, and the specifics of your request. Postal requests might
        take longer (due to mailing time and manual processing), but we will
        handle them with the same care. If you send a postal request, consider
        providing an email or phone contact so we can reach you faster with any
        questions or the outcome.
      </li>
      <li>
        <strong>Telephone:</strong> You may call our office (the number is on
        our website) if you have a quick question or want to notify us of
        something urgent. However, for formal rights exercises like getting a
        copy of data, we will likely ask you to put it in writing (email or
        letter) just so there is a clear record, and we can authenticate
        properly. But we’re happy to assist or guide you on the phone if you’re
        not sure how to proceed.
      </li>
    </ul>
    <p>
      <strong>Response Time:</strong> We strive to acknowledge requests as soon as
      possible – usually within a few days of receipt – and to fulfil them within
      one month as required. If a request is complex or we have many requests at
      once, the law allows us to take up to an additional two months, but if we
      ever need that, we’ll inform you within the first month and explain why. For
      example, if you requested all data including recorded calls and we have to
      retrieve archives, we might need more time. But we’ll keep you informed.
    </p>
    <p>
      <strong>What to Expect:</strong> When you make a rights request, we will do
      our best to accommodate it fully. If we cannot (due to a legal exemption or
      other reason), we will clearly explain why. For access requests, we will
      provide the data securely (likely via a secure download link, or encrypted
      email, etc.). For correction, we’ll confirm once we’ve corrected it. For
      deletion, we’ll confirm deletion (or specify what was deleted and what
      couldn’t be and why). For objections or restrictions, we will confirm the
      action taken on our side. We log these requests, so we have a record of
      compliance.
    </p>
    <p>
      We do not penalize or discriminate against anyone for exercising their
      rights. If you request deletion of some data, we won’t drop you from the
      service unless that data was essential to providing the service (and then
      we’ll discuss alternatives with you). Our goal is to facilitate your rights
      in a way that also allows you to continue benefiting from our services
      wherever possible.
    </p>
    <p>
      <strong>Data Protection Officer (DPO):</strong> As mentioned, we have a DPO
      you can contact. The DPO’s role is to oversee our data protection strategy
      and compliance, and to be an independent advocate for data subjects within
      our organisation. You can reach out to the DPO not only for rights requests
      but for any privacy-related inquiry or complaint. The DPO will treat your
      concerns confidentially and with importance.
    </p>
    <p>
      Finally, if you’re unsatisfied with how we handled your request or any
      aspect of your rights, please let us know. We appreciate feedback and see it
      as a chance to improve. We’re dedicated to respecting your rights and
      protecting your privacy – it’s fundamental to the trust needed in providing
      healthcare services.
    </p>
    <p>
      <strong>Updates to This Privacy Notice</strong>
    </p>
    <p>
      We may update this Privacy Notice from time to time to reflect changes in
      our services or to ensure ongoing compliance with relevant laws. If we make
      significant changes, we will notify users through appropriate channels – for
      example, via email to account holders or via an in-app notification. The
      “Effective Date” at the top will always indicate when the notice was last
      revised. We encourage you to review this notice periodically to stay
      informed about how we are protecting your information. Rest assured, we will
      not reduce your rights under this Privacy Notice or under applicable data
      protection laws without your consent. Any updates will aim to maintain or
      improve transparency. If you have any questions about the changes, you can
      contact our DPO for clarification.
    </p>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicyContent"
};
</script>
